import type { SkillDetailDto, SkillPreviewDto, SkillTryDto } from '@dbrain/shared';
import { Alert, Button, Field, IconButton, Icons, PageLoading, Switch, TabPanel, Tabs, TextArea, TextInput } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { capKeys, useSkill, useSkills } from '../api/capabilities.js';
import { api, upload } from '../api/client.js';
import { Markdown } from '../components/chat/Markdown.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import type { EditorState } from '../components/skills/SkillAddDialog.js';
import { estimate, formatSize, ScopeMenu, targetBody, zipFiles, type Target } from '../components/skills/SkillParts.js';
import { emptyForm, formFromSkillMd, renderPreview, type FormState } from '@dbrain/app-core/skill-form';

/**
 * Writing a skill (SA-054; docs/design/mockups/SkillYaz.dc.html) on a page
 * of its own. The form and the raw SKILL.md edit the same skill; who can
 * use a new one is chosen at the top. On the right: how it looks in the
 * "/" menu, checks as one types, and "Kaydetmeden dene" — one test turn with
 * the draft, not saved, counted toward the user's budget. A draft from a
 * conversation opens here filled in.
 */
const NAME = /^[a-z0-9][a-z0-9-]{0,63}$/;
const DESCRIPTION_SOFT = 200;
const SCRIPT = /\.(py|sh|bash|js|mjs|cjs|ts|rb|pl)$/i;
const TEMPLATE = /\.(xlsx|xltx|docx|dotx|pptx|potx|html|css|png|jpe?g|svg|ttf|otf|csv)$/i;

/** Where an added file goes in the skill's folder, by its kind. */
const placeOf = (name: string) => (SCRIPT.test(name) ? `scripts/${name}` : TEMPLATE.test(name) ? `templates/${name}` : `references/${name}`);
const RUNNERS: Record<string, string> = { py: 'python3', sh: 'bash', bash: 'bash', js: 'node', mjs: 'node', cjs: 'node' };
/** The command that runs an added script, as a grant ("Bash(python3 scripts/x.py *)"). */
const grantFor = (name: string) => {
  const runner = RUNNERS[name.split('.').pop()?.toLowerCase() ?? ''];
  return runner ? `Bash(${runner} ${placeOf(name)} *)` : null;
};

type Check = { tone: 'ok' | 'warn' | 'bad'; text: string };

function useChecks(form: FormState, raw: string | null, taken: string | null): Check[] {
  const { t } = useTranslation();
  const fields = raw !== null ? formFromSkillMd(raw) : form;
  const checks: Check[] = [];
  if (raw !== null && !/^---\n[\s\S]*?\n---/.test(raw.replace(/\r\n/g, '\n').trimStart())) checks.push({ tone: 'bad', text: t('skills.editor.checks.frontmatter') });
  const name = fields.name.trim();
  if (!name) checks.push({ tone: 'bad', text: t('skills.editor.checks.noName') });
  else if (!NAME.test(name)) checks.push({ tone: 'bad', text: t('skills.editor.checks.badName') });
  else if (taken) checks.push({ tone: 'warn', text: t('skills.editor.checks.taken', { level: taken }) });
  else checks.push({ tone: 'ok', text: t('skills.editor.checks.name') });
  const description = fields.description.trim();
  if (!description) checks.push({ tone: 'bad', text: t('skills.editor.checks.noDescription') });
  else if (description.length < 20) checks.push({ tone: 'warn', text: t('skills.editor.checks.shortDescription') });
  else if (description.length > DESCRIPTION_SOFT) checks.push({ tone: 'warn', text: t('skills.editor.checks.longDescription', { max: DESCRIPTION_SOFT }) });
  else checks.push({ tone: 'ok', text: t('skills.editor.checks.description') });
  if (!fields.whenToUse.trim()) checks.push({ tone: 'warn', text: t('skills.editor.checks.noWhen') });
  else checks.push({ tone: 'ok', text: t('skills.editor.checks.when') });
  const body = fields.body.trim();
  if (!body) checks.push({ tone: 'bad', text: t('skills.editor.checks.noBody') });
  else if (!/^\s{0,2}\d+[.)]\s/m.test(body)) checks.push({ tone: 'warn', text: t('skills.editor.checks.noSteps') });
  else checks.push({ tone: 'ok', text: t('skills.editor.checks.steps') });
  if (body.length > 12_000) checks.push({ tone: 'warn', text: t('skills.editor.checks.longBody') });
  return checks;
}

function CheckIcon({ tone }: { tone: Check['tone'] }) {
  if (tone === 'ok') return <Icons.done size={16} className="check__icon check__icon--ok" />;
  if (tone === 'warn') return <Icons.warning size={16} className="check__icon check__icon--warn" />;
  return <Icons.error size={16} className="check__icon check__icon--bad" />;
}

export function SkillEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { skillKey } = useParams();
  const state = (location.state ?? {}) as EditorState;
  const existing = useSkill(skillKey ?? null);
  if (skillKey && existing.isPending) return <PageLoading label={t('common.loading')} />;
  if (skillKey && !existing.data) {
    return (
      <div className="page">
        <ErrorAlert error={existing.error} action={<Link to="/skills">{t('skills.title')}</Link>} />
      </div>
    );
  }
  return <Editor key={skillKey ?? 'new'} skill={existing.data} state={state} onDone={(saved) => {
    void queryClient.invalidateQueries({ queryKey: capKeys.skillsAll });
    void navigate(`/skills?open=${encodeURIComponent(saved.key)}`, { replace: true });
  }} onCancel={() => void navigate(skillKey ? `/skills?open=${encodeURIComponent(skillKey)}` : '/skills')} />;
}

function Editor({ skill, state, onDone, onCancel }: { skill: SkillDetailDto | undefined; state: EditorState; onDone: (saved: SkillDetailDto) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const ids = { name: useId(), hint: useId(), description: useId(), when: useId(), body: useId(), raw: useId(), note: useId(), test: useId() };
  const initial = useMemo<FormState>(() => {
    if (skill) return formFromSkillMd(skill.skillMd);
    if (state.draft) return { ...emptyForm, ...state.draft.form, whenToUse: state.draft.form.whenToUse ?? '', argumentHint: state.draft.form.argumentHint ?? '', allowedTools: '' };
    return emptyForm;
  }, [skill, state.draft]);
  const [tab, setTab] = useState<'form' | 'raw'>('form');
  const [form, setForm] = useState<FormState>(initial);
  const [raw, setRaw] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [target, setTarget] = useState<Target>(state.target ?? { level: 'user' });
  const [files, setFiles] = useState<File[]>([]);
  const [grantScripts, setGrantScripts] = useState(true);
  const [prompt, setPrompt] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const all = useSkills({});
  const set = (key: keyof FormState) => (value: string) => setForm((current) => ({ ...current, [key]: value }));
  const scripts = files.filter((f) => SCRIPT.test(f.name));
  // a script added here runs without asking only when the user says so (D-03)
  const scriptGrants = grantScripts ? scripts.map((f) => grantFor(f.name)).filter((g): g is string => g !== null) : [];
  const withGrants = (value: FormState): FormState => {
    const lines = value.allowedTools.split('\n').map((s) => s.trim()).filter(Boolean);
    for (const grant of scriptGrants) if (!lines.includes(grant)) lines.push(grant);
    return { ...value, allowedTools: lines.join('\n') };
  };
  /** The SKILL.md as it is now, from the raw text once touched, else from the fields. */
  const skillMd = () => (raw !== null ? raw : renderPreview(withGrants(form)));
  const fields = raw !== null ? formFromSkillMd(raw) : form;
  const taken = !skill && fields.name.trim() ? (all.data ?? []).find((s) => s.name === fields.name.trim()) : undefined;
  const checks = useChecks(form, raw, taken ? t(`skills.levels.${taken.level}`) : null);
  const blocked = checks.some((c) => c.tone === 'bad');

  const save = useMutation({
    mutationFn: async (): Promise<SkillDetailDto> => {
      const body = raw !== null ? { skillMd: raw } : { form: { ...formPayload(withGrants(form)) } };
      if (skill) return api<SkillDetailDto>('PUT', `/api/skills/${skill.id}`, { ...body, ...(note.trim() ? { changeNote: note.trim() } : {}) });
      if (files.length === 0) return api<SkillDetailDto>('POST', '/api/skills', { ...body, ...targetBody(target), source: state.draft ? 'conversation' : 'created' });
      // with files: the folder goes through the same preview and install as an upload (D-13)
      const entries = [{ path: 'SKILL.md', data: new TextEncoder().encode(skillMd()) }];
      for (const file of files) entries.push({ path: placeOf(file.name), data: new Uint8Array(await file.arrayBuffer()) });
      const preview = await upload<SkillPreviewDto>('POST', '/api/skills/upload', await zipFiles(entries), { 'x-file-name': encodeURIComponent(`${fields.name || 'skill'}.zip`), 'content-type': 'application/octet-stream' });
      return api<SkillDetailDto>('POST', '/api/skills/install', { previewId: preview.previewId, ...targetBody(target) });
    },
    onSuccess: onDone,
  });
  const tryIt = useMutation({
    mutationFn: () => api<SkillTryDto>('POST', '/api/skills/try', { skillMd: skillMd(), prompt: prompt.trim(), ...(target.level === 'workspace' && target.workspaceId ? { workspaceId: target.workspaceId } : {}) }),
  });
  const switchTab = (next: 'form' | 'raw') => {
    if (next === tab) return;
    if (next === 'raw') setRaw(skillMd());
    else if (raw !== null) {
      // the raw text, read back into the fields
      setForm(formFromSkillMd(raw));
      setRaw(null);
    }
    setTab(next);
  };
  const argumentHint = fields.argumentHint.trim();

  return (
    <div className="skill-editor-page" data-testid="skill-editor">
      <header className="editor-bar">
        <button type="button" className="back-link" onClick={onCancel}>
          <Icons.chevronLeft size={14} />
          {t('skills.title')}
        </button>
        <span className="editor-bar__divider" aria-hidden="true" />
        <h1 className="editor-bar__title">{skill ? t('skills.editor.editTitle', { name: skill.name }) : t('skills.editor.newTitle')}</h1>
        <Tabs
          tabsId="skill-editor"
          variant="segmented"
          label={t('skills.editor.mode')}
          value={tab}
          onChange={switchTab}
          tabs={[
            { id: 'form', label: t('skills.editor.form'), 'data-testid': 'skill-tab-form' },
            { id: 'raw', label: <span className="mono">SKILL.md</span>, 'data-testid': 'skill-tab-raw' },
          ]}
        />
        <div className="editor-bar__actions">
          {skill ? null : <ScopeMenu value={target} onChange={setTarget} testId="skill-editor-scope" />}
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button busy={save.isPending} disabled={blocked} onClick={() => save.mutate()} data-testid="skill-save">
            {skill ? t('skills.editor.saveVersion') : t('common.save')}
          </Button>
        </div>
      </header>

      <div className="editor-layout">
        <TabPanel tabsId="skill-editor" id={tab} className="editor-main">
          {state.draft ? <Alert tone="info" title={t('skills.editor.fromConversation')} /> : null}
          <ErrorAlert error={save.error} />
          {tab === 'form' ? (
            <div className="editor-form">
              <div className="editor-form__pair">
                <div className="ui-field">
                  <label className="ui-field__label" htmlFor={ids.name}>
                    {t('skills.editor.name')}
                  </label>
                  <span className={skill ? 'name-input name-input--locked' : 'name-input'}>
                    <span className="name-input__slash" aria-hidden="true">
                      /
                    </span>
                    <input
                      id={ids.name}
                      value={form.name}
                      onChange={(e) => set('name')(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                      disabled={Boolean(skill)}
                      spellCheck={false}
                      aria-describedby={`${ids.name}-hint`}
                      data-testid="skill-name"
                    />
                    {NAME.test(form.name.trim()) && !taken ? <Icons.done size={16} className="check__icon check__icon--ok" aria-label={t('skills.editor.nameOk')} role="img" /> : null}
                  </span>
                  <p id={`${ids.name}-hint`} className="ui-field__hint">
                    {form.name.trim() ? t('skills.editor.nameHintNamed', { name: form.name.trim() }) : t('skills.editor.nameHint')}
                  </p>
                </div>
                <Field
                  label={
                    <>
                      {t('skills.editor.argumentHint')} <span className="field-optional">{t('common.optional')}</span>
                    </>
                  }
                  hint={t('skills.editor.argumentHintHint')}
                >
                  {({ id, describedBy }) => <TextInput id={id} aria-describedby={describedBy} value={form.argumentHint} onChange={(e) => set('argumentHint')(e.target.value)} data-testid="skill-argument-hint" />}
                </Field>
              </div>
              <div className="ui-field">
                <div className="field-head">
                  <label className="ui-field__label" htmlFor={ids.description}>
                    {t('skills.editor.description')}
                  </label>
                  <span className={form.description.length > DESCRIPTION_SOFT ? 'field-count field-count--over' : 'field-count'} aria-live="polite">
                    {form.description.length} / {DESCRIPTION_SOFT}
                  </span>
                </div>
                <TextInput id={ids.description} aria-describedby={`${ids.description}-hint`} value={form.description} onChange={(e) => set('description')(e.target.value)} data-testid="skill-description" />
                <p id={`${ids.description}-hint`} className="ui-field__hint">
                  {t('skills.editor.descriptionHint')}
                </p>
              </div>
              <Field label={t('skills.editor.whenToUse')} hint={t('skills.editor.whenToUseHint')}>
                {({ id, describedBy }) => <TextInput id={id} aria-describedby={describedBy} value={form.whenToUse} onChange={(e) => set('whenToUse')(e.target.value)} data-testid="skill-when" />}
              </Field>
              <Field label={t('skills.editor.body')} hint={t('skills.editor.bodyHint', { tokens: estimate(form.body.length) })}>
                {({ id, describedBy }) => <TextArea id={id} aria-describedby={describedBy} rows={10} className="editor-form__body" value={form.body} onChange={(e) => set('body')(e.target.value)} data-testid="skill-body" />}
              </Field>
              {skill ? (
                skill.files.length > 1 ? (
                  <p className="muted">{t('skills.editor.filesKept', { count: skill.files.length - 1 })}</p>
                ) : null
              ) : (
                <>
                  <button type="button" className="add-files" onClick={() => fileInput.current?.click()} data-testid="skill-add-files">
                    <Icons.plus size={18} />
                    <span className="add-files__text">
                      <span className="add-files__title">
                        {t('skills.editor.addFiles')} <span className="field-optional">{t('common.optional')}</span>
                      </span>
                      <span className="add-files__hint">{t('skills.editor.addFilesHint')}</span>
                    </span>
                  </button>
                  <input
                    ref={fileInput}
                    type="file"
                    multiple
                    className="ui-visually-hidden"
                    tabIndex={-1}
                    aria-hidden="true"
                    onChange={(e) => {
                      const picked = [...(e.target.files ?? [])];
                      e.target.value = '';
                      setFiles((current) => [...current, ...picked.filter((f) => !current.some((c) => c.name === f.name))]);
                    }}
                    data-testid="skill-files-input"
                  />
                  {files.length > 0 ? (
                    <ul className="editor-files" data-testid="skill-editor-files">
                      {files.map((file) => (
                        <li key={file.name}>
                          <code>{placeOf(file.name)}</code>
                          <span className="muted">{formatSize(file.size)}</span>
                          <IconButton size="sm" label={t('skills.add.removeFile')} icon={<Icons.close size={14} />} onClick={() => setFiles((current) => current.filter((f) => f !== file))} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {scripts.length > 0 ? (
                    <label className="ui-switch-row editor-grant">
                      <Switch checked={grantScripts} onChange={(e) => setGrantScripts(e.target.checked)} data-testid="skill-grant-scripts" />
                      <span>
                        {t('skills.editor.grantScripts')}
                        <span className="muted"> · {scripts.map((f) => placeOf(f.name)).join(', ')}</span>
                      </span>
                    </label>
                  ) : null}
                </>
              )}
              {skill ? (
                <Field label={t('skills.editor.changeNote')}>
                  {({ id }) => <TextInput id={id} value={note} onChange={(e) => setNote(e.target.value)} />}
                </Field>
              ) : null}
            </div>
          ) : (
            <Field label="SKILL.md" hint={t('skills.editor.rawHint')}>
              {({ id, describedBy }) => <TextArea id={id} aria-describedby={describedBy} rows={22} className="skill-raw-editor" value={raw ?? ''} onChange={(e) => setRaw(e.target.value)} spellCheck={false} data-testid="skill-raw" />}
            </Field>
          )}
        </TabPanel>

        <aside className="editor-side" aria-label={t('skills.editor.side')}>
          <section className="editor-side__block">
            <h2>{t('skills.editor.menuPreview')}</h2>
            <div className="slash-preview" data-testid="skill-slash-preview">
              <span className="skill-preview__tile skill-preview__tile--sm" aria-hidden="true">
                <Icons.skills size={15} />
              </span>
              <span className="slash-preview__text">
                <span className="slash-preview__name">
                  /{fields.name.trim() || t('skills.editor.namePlaceholder')}
                  {argumentHint ? <span className="slash-preview__args"> {argumentHint}</span> : null}
                </span>
                <span className="slash-preview__description">{fields.description.trim() || t('skills.editor.descriptionPlaceholder')}</span>
              </span>
            </div>
          </section>
          <section className="editor-side__block">
            <h2>{t('skills.editor.checksTitle')}</h2>
            <ul className="checks" data-testid="skill-checks">
              {checks.map((check) => (
                <li key={check.text} className={`check check--${check.tone}`}>
                  <CheckIcon tone={check.tone} />
                  {check.text}
                </li>
              ))}
            </ul>
          </section>
          <section className="try-box" aria-labelledby="skill-try-title" data-testid="skill-try">
            <h2 id="skill-try-title">{t('skills.editor.try')}</h2>
            <form
              className="try-box__form"
              onSubmit={(e) => {
                e.preventDefault();
                if (prompt.trim() && !blocked) tryIt.mutate();
              }}
            >
              <label htmlFor={ids.test} className="ui-visually-hidden">
                {t('skills.editor.tryLabel')}
              </label>
              <input id={ids.test} className="ui-input try-box__input" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={t('skills.editor.tryPlaceholder')} data-testid="skill-try-input" />
              <Button type="submit" icon={<Icons.play size={13} />} busy={tryIt.isPending} disabled={!prompt.trim() || blocked} data-testid="skill-try-run">
                {t('skills.editor.tryRun')}
              </Button>
            </form>
            <p className="try-box__note">{t('skills.editor.tryNote')}</p>
            <ErrorAlert error={tryIt.error} />
            {tryIt.data ? (
              <>
                <p className={tryIt.data.chosen ? 'try-box__status try-box__status--ok' : 'try-box__status try-box__status--warn'} data-testid="skill-try-status" data-chosen={tryIt.data.chosen}>
                  {tryIt.data.chosen ? <Icons.done size={14} /> : <Icons.warning size={14} />}
                  {tryIt.data.chosen
                    ? t('skills.editor.tryChosen', { seconds: Math.max(1, Math.round(tryIt.data.durationMs / 1000)) })
                    : tryIt.data.other
                      ? t('skills.editor.tryOther', { other: tryIt.data.other, seconds: Math.max(1, Math.round(tryIt.data.durationMs / 1000)) })
                      : t('skills.editor.tryNotChosen', { name: fields.name.trim(), seconds: Math.max(1, Math.round(tryIt.data.durationMs / 1000)) })}
                </p>
                <div className="try-box__answer" data-testid="skill-try-answer">
                  <Markdown source={tryIt.data.text} />
                </div>
              </>
            ) : null}
          </section>
        </aside>
      </div>
    </div>
  );
}

/** The form as the API takes it. */
function formPayload(form: FormState) {
  return {
    name: form.name.trim(),
    description: form.description.trim(),
    ...(form.whenToUse.trim() ? { whenToUse: form.whenToUse.trim() } : {}),
    ...(form.argumentHint.trim() ? { argumentHint: form.argumentHint.trim() } : {}),
    ...(form.allowedTools.trim()
      ? {
          allowedTools: form.allowedTools
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean),
        }
      : {}),
    body: form.body,
  };
}
